import { useState } from 'react';
import styled from 'styled-components';
import {
  SemanticEssentialColor,
  StatusIndicator,
  Type,
  TooltipTrigger,
  IconInformationAlt,
  VisuallyHidden,
  spacer24,
  spacer8,
} from '@spotify-internal/encore-web/core';
import { useT } from '@spotify-internal/i18n-core';
import {
  MusicVideoClearanceStatusType,
  Patch,
} from '@hooks/useAlbumInfo/types';
import { PillInfo } from '@components/shared/PillInfo/PillInfo';
import { TooltipWithPadding, Row, RowCell } from '@components/shared/styles';
import { EMPTY_VALUE } from '@helpers/constants';
import { fromISOToMomentObject } from '@helpers/dateTimeUtils';
import { getTerritoryName } from '@helpers/territories';
import { PillsContainer } from '../RelatedTrack/styles';
import { PaddedStack } from './AvailabilityFlagStatus';

const PaddedStatusContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 96%;
`;
export const FullRow = styled(Row)`
  width: 100%;
`;
type AvailabilityClearanceStatusProps = {
  patches: Patch[];
};

export const AvailabilityClearanceStatus = ({
  patches,
}: AvailabilityClearanceStatusProps) => {
  const t = useT();
  const [showLabelTooltip, toggleShowLabelTooltip] = useState(false);
  const clearanceStatusIndicator = (status: MusicVideoClearanceStatusType) => {
    let humanReadableStatus = '';
    let color: SemanticEssentialColor;
    switch (status) {
      case MusicVideoClearanceStatusType.MUSIC_VIDEO_CLEARANCE_STATUS_TYPE_CLEARED:
      case MusicVideoClearanceStatusType.MUSIC_VIDEO_CLEARANCE_STATUS_TYPE_CLEARED_BY_OVERRIDE:
        humanReadableStatus = t(
          'atlas.labels.cleared',
          'Cleared',
          'Release has had its rights clearance approved',
        );

        color = 'essentialPositive';
        break;

      case MusicVideoClearanceStatusType.MUSIC_VIDEO_CLEARANCE_STATUS_TYPE_IN_PROGRESS:
        humanReadableStatus = t(
          'atlas.labels.in_progress',
          'In progress',
          'Release rights clearance is still being processed',
        );

        color = 'essentialSubdued';
        break;
      case MusicVideoClearanceStatusType.MUSIC_VIDEO_CLEARANCE_STATUS_TYPE_UNKNOWN:
      case MusicVideoClearanceStatusType.MUSIC_VIDEO_CLEARANCE_STATUS_TYPE_NOT_CLEARED:
      default:
        humanReadableStatus = t(
          'atlas.labels.not_cleared',
          'Not cleared',
          'Release has not had its rights clearance approved',
        );

        color = 'essentialNegative';
        break;
    }

    return (
      <PaddedStatusContainer>
        <StatusIndicator semanticColor={color}>
          <Type variant="viola" semanticColor="textSubdued">
            {humanReadableStatus}
          </Type>
        </StatusIndicator>
        {status ===
          MusicVideoClearanceStatusType.MUSIC_VIDEO_CLEARANCE_STATUS_TYPE_IN_PROGRESS && (
          <TooltipTrigger
            overlay={
              showLabelTooltip && (
                <TooltipWithPadding centeredText>
                  <Type
                    variant="viola"
                    dangerouslySetInnerHTML={{
                      __html: t(
                        'atlas.warnings.rights_clearance_duration',
                        'Clearance status for this territory is processing and can take up to 72 hours for the status to be reflected.',
                        'Information about the rights clearance processes duration',
                      ),
                    }}
                  />
                </TooltipWithPadding>
              )
            }
            placement={TooltipTrigger.top}
            onMouseEnter={() => toggleShowLabelTooltip(true)}
            onMouseLeave={() => toggleShowLabelTooltip(false)}
          >
            <IconInformationAlt
              iconSize={16}
              aria-label="More information for clearance status"
              semanticColor="textSubdued"
            />
            <VisuallyHidden>More information help icon</VisuallyHidden>
          </TooltipTrigger>
        )}
      </PaddedStatusContainer>
    );
  };

  return (
    <PaddedStack>
      <div>
        <Type
          variant="cello"
          paddingBottom={spacer8}
          as="h2"
          semanticColor="textBase"
        >
          {t(
            'atlas.labels.clearance_status',
            'Clearance status',
            'Label for rights clearance status of a blocked release',
          )}
        </Type>

        <Type
          variant="viola"
          paddingBottom={spacer24}
          dangerouslySetInnerHTML={{
            __html: t(
              'atlas.warnings.rights_clearance_failed_or_pending',
              'Music video has not been cleared for distribution in the following markets. Check the <a href="mailto:content-ops+ingestion@spotify.com">Frequently Asked Questions</a> for more details.',
              'Description of the current rights clearance issue for a blocked release',
            ),
          }}
        />
      </div>
      <PillsContainer>
        {patches.map(patch => (
          <FullRow key={patch.country}>
            <RowCell marginRight>
              <PillInfo
                label={`${patch.country} (${getTerritoryName(patch.country)})`}
                value={clearanceStatusIndicator(patch.clearanceRequestStatus)}
                tooltipMessage={undefined}
                backgroundColor="backgroundHighlight"
              />
            </RowCell>
            <RowCell marginLeft>
              <PillInfo
                label={t(
                  'atlas.labels.last_updated',
                  'Last updated',
                  'Label text for date status was last updated (i.e. 2022-08-12 00:00:00)',
                )}
                value={
                  fromISOToMomentObject(patch.lastUpdated)?.format(
                    'YYYY-MM-DD HH:MM:SS',
                  ) || EMPTY_VALUE
                }
                tooltipMessage={undefined}
                backgroundColor="backgroundHighlight"
              />
            </RowCell>
          </FullRow>
        ))}
      </PillsContainer>
    </PaddedStack>
  );
};
